import { get } from "lodash-es";
import { GetHeadToHeadPlayerStatsTabQuery } from "@/graphql/gql/graphql";

type Player = NonNullable<
  NonNullable<
    NonNullable<GetHeadToHeadPlayerStatsTabQuery["event"]>["awayTeam"]
  >["players"]
>[number];

export enum PositionFilter {
  QUARTERBACK = "quarterback",
  RUNNING_BACK = "runningBack",
  RECEIVER = "receiver",
  TIGHT_END = "tightEnd",
  FLEX = "flex",
  KICKER = "kicker",
  DEFENSE = "defenseSpecialTeams",
}

export interface PlayerDatum {
  name: string;
  numberFirePlayerId: number;
  position: string;
  jerseyNumber: number | null | undefined;
  teamShortCode: string;
  teamLogoSrc: string;
  jerseyLogoSrc: string;
  passingAttempts: number;
  passingCompletions: number;
  passingCompletionPercentage: string;
  passingYards: number;
  passingTouchdowns: number;
  interceptionsThrown: number;
  rushingAttempts: number;
  rushingYards: number;
  rushingYardsPerAttempt: string;
  rushingTouchdowns: number;
  receivingReceptions: number;
  receivingTargets: number;
  receivingYards: number;
  receivingYardsPerReception: string;
  receivingTouchdowns: number;
  extraPointsAttempted: number;
  extraPointsMade: number;
  extraPointsMissed: number;
  fieldGoals: {
    total: {
      attempted: number;
      made: number;
      missed: number;
    };
    zeroToThirtyNineYards: {
      attempted: number;
      made: number;
      missed: number;
    };
    fortyToFortyNineYards: {
      attempted: number;
      made: number;
      missed: number;
    };
    fiftyPlusYards: {
      attempted: number;
      made: number;
      missed: number;
    };
  };
  fumblesForced: number;
  fumblesLost: number;
  fumblesRecovered: number;
  // "sacks" applies appropriately to both QBs and D(efense)
  sacks: number;
  // Defense-specific
  interceptionsCaught: number;
  passingYardsAllowed: number;
  rushingYardsAllowed: number;
  pointsAllowed: number;
  gamesPlayed: number;
}

export const playerToStatistics = (
  player: Player,
  seasonYear: number
): PlayerDatum => {
  const statsForSeason = player?.statistics?.seasons?.find(
    (season) =>
      season?.seasonInfo?.seasonYear === seasonYear &&
      season.seasonInfo?.seasonType === "REG"
  );
  const playerStats = statsForSeason?.seasonStatistics;

  const rushingAttempts = get(playerStats, "rushing.attempts.cumulative", 0);
  const rushingYards = get(playerStats, "rushing.yards.cumulative", 0);
  let rushingYardsPerAttempt: string | number =
    rushingAttempts > 0 ? rushingYards / rushingAttempts : 0;
  rushingYardsPerAttempt = rushingYardsPerAttempt.toFixed(1);

  const receivingReceptions = get(
    playerStats,
    "receiving.receptions.cumulative",
    0
  );
  const receivingYards = get(playerStats, "receiving.yards.cumulative", 0);
  let receivingYardsPerReception: string | number =
    rushingAttempts > 0 ? rushingYards / rushingAttempts : 0;
  receivingYardsPerReception = receivingYardsPerReception.toFixed(1);

  return {
    gamesPlayed: get(playerStats, "gamesPlayed", 0) ?? 0,
    name: player?.name ?? "",
    numberFirePlayerId: player?.numberfireId ?? 0,
    jerseyNumber: player?.jerseyNumber,
    teamShortCode: player?.teamInfo?.code ?? "",
    teamLogoSrc: player?.teamInfo?.logo?.svg ?? "",
    jerseyLogoSrc: player?.teamInfo?.teamJersey?.svg ?? "",
    position: player?.position ?? "Unknown",
    passingAttempts: get(playerStats, "passing.attempts.cumulative", 0),
    passingCompletions: get(playerStats, "passing.completions.cumulative", 0),
    passingCompletionPercentage: get(
      playerStats,
      "passing.ratio.percentDisplay",
      "-"
    ),
    passingTouchdowns: get(playerStats, "passing.touchdowns.cumulative", 0),
    passingYards: get(playerStats, "passing.yards.cumulative", 0),
    interceptionsThrown: get(
      playerStats,
      "passing.interceptions.cumulative",
      0
    ),
    rushingAttempts,
    rushingYards,
    rushingYardsPerAttempt,
    rushingTouchdowns: get(playerStats, "rushing.touchdowns.cumulative", 0),
    receivingReceptions,
    receivingTargets: get(playerStats, "receiving.targets.cumulative", 0),
    receivingYards,
    receivingYardsPerReception,
    receivingTouchdowns: get(playerStats, "receiving.touchdowns.cumulative", 0),
    extraPointsAttempted: get(
      playerStats,
      "kicking.extraPoints.attempted.cumulative",
      0
    ),
    extraPointsMade: get(playerStats, "kicking.extraPoints.made.cumulative", 0),
    extraPointsMissed: get(
      playerStats,
      "kicking.extraPoints.missed.cumulative",
      0
    ),
    fieldGoals: {
      total: {
        attempted: get(
          playerStats,
          "kicking.fieldGoals.cumulative.attempted.cumulative",
          0
        ),
        made: get(
          playerStats,
          "kicking.fieldGoals.cumulative.made.cumulative",
          0
        ),
        missed: get(
          playerStats,
          "kicking.fieldGoals.cumulative.missed.cumulative",
          0
        ),
      },
      zeroToThirtyNineYards: {
        attempted: get(
          playerStats,
          "kicking.fieldGoals.zeroToThirtyNineYards.attempted.cumulative",
          0
        ),
        made: get(
          playerStats,
          "kicking.fieldGoals.zeroToThirtyNineYards.made.cumulative",
          0
        ),
        missed: get(
          playerStats,
          "kicking.fieldGoals.zeroToThirtyNineYards.missed.cumulative",
          0
        ),
      },
      fortyToFortyNineYards: {
        attempted: get(
          playerStats,
          "kicking.fieldGoals.fortyToFortyNineYards.attempted.cumulative",
          0
        ),
        made: get(
          playerStats,
          "kicking.fieldGoals.fortyToFortyNineYards.made.cumulative",
          0
        ),
        missed: get(
          playerStats,
          "kicking.fieldGoals.fortyToFortyNineYards.missed.cumulative",
          0
        ),
      },
      fiftyPlusYards: {
        attempted: get(
          playerStats,
          "kicking.fieldGoals.fiftyPlusYards.attempted.cumulative",
          0
        ),
        made: get(
          playerStats,
          "kicking.fieldGoals.fiftyPlusYards.made.cumulative",
          0
        ),
        missed: get(
          playerStats,
          "kicking.fieldGoals.fiftyPlusYards.missed.cumulative",
          0
        ),
      },
    },
    fumblesForced: get(playerStats, "fumbles.forced.cumulative", 0),
    fumblesLost: get(playerStats, "fumbles.lost.cumulative", 0),
    fumblesRecovered: get(playerStats, "fumbles.recovered.cumulative", 0),
    sacks: get(playerStats, "sacks.cumulative", 0),
    interceptionsCaught: get(
      playerStats,
      "defense.interceptions.cumulative",
      0
    ),
    passingYardsAllowed: get(
      playerStats,
      "defense.passingYardsAllowed.cumulative",
      0
    ),
    rushingYardsAllowed: get(
      playerStats,
      "defense.rushingYardsAllowed.cumulative",
      0
    ),
    pointsAllowed: get(playerStats, "defense.pointsAllowed.cumulative", 0),
  };
};

/**
 * Determines whether to display the previous season's year for player stats in the pregame player stats tab before week 2 of the NFL season.
 *
 * Context:
 * - The Player Stats module is only shown in the pregame state.
 * - For week 1, NumberFire returns stats from the previous season (since current stats would all be zero).
 * - NumberFire manually updates stats, usually on Tuesday morning after week 1 ends, but the exact timing can vary.
 * - This function helps ensure the correct season year is shown, accounting for the manual update window.
 */
export const statsArePreviousSeasonYear = (
  weekOneEnd: string,
  players: PlayerDatum[]
) => {
  const today = new Date();
  const weekOneEndDate = new Date(weekOneEnd);
  const weekOnePlusThreeDays = new Date(weekOneEnd);
  weekOnePlusThreeDays.setDate(weekOneEndDate.getDate() + 3);
  if (today < weekOneEndDate) {
    return true;
    // In this case NumberFire will always default to week 1 which will cause them to show previous year stats.
  } else if (today > weekOneEndDate && today < weekOnePlusThreeDays) {
    /**
     * The last game of NFL week 1 is typically the Monday night game.
     * NumberFire is expected to update stats to the current season before week 2 starts (by Thursday night, i.e., Monday + 3 days).
     * During the period between Monday night and Thursday night, stats may reflect either the previous or current season, depending on when the manual update occurs.
     * Therefore, we need to check if the stats are up-to-date in order to display the correct season year.
     */
    if (players.some((player) => player.gamesPlayed > 1)) {
      return true;
    }
  }
  return false;
};
